/**
 * The Cortex Console Instances blade (harness row CC3-11; spec.md 8A ViewInstances,
 * requirements.md FR-11 through FR-14, design.md section 11).
 *
 * What this component does: render the answer `GET /api/cortex/console/instances`
 * gave - the shared environments it named, each with the facts the server already
 * decided. Git checkouts moved to their own blade (CC-3B, `GitCheckoutsBlade.tsx`);
 * this blade no longer renders them (forbidden path
 * `duplicate_checkout_rendering_on_instances_blade`).
 *
 * What it deliberately does NOT do:
 *
 * - decide who may see a row. Eligibility was applied once by the read layer; a row
 *   that arrives here is one the caller is entitled to;
 * - compute a drift state. `drift_state` and `drift_reasons` are the server's, from
 *   its own stored probe row - this blade never compares a version or a setting name
 *   itself;
 * - start, stop or apply anything. There is no remediation control here at all
 *   (spec.md 8C row NR-2): the blade reports, a person acts through the existing
 *   pipeline.
 */
import React from "react";
import { ContentRegionLoader } from "../components/loading";
import type {
  ConsoleInstancesResponse,
  ConsoleRefusal,
  ConsoleSharedInstanceRow,
} from "../api/types";
import styles from "./InstancesBlade.module.css";

const DRIFT_STATE_LABELS: Record<string, string> = {
  aligned: "Aligned",
  drifted: "Drifted",
  unknown: "Unknown",
};

const LIVENESS_LABELS: Record<string, string> = {
  live: "Live",
  silent: "Silent",
  abandoned: "Abandoned",
};

function humanise(id: string): string {
  const words = id.replace(/_/g, " ").trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : id;
}

export function driftStateLabel(state: string): string {
  return DRIFT_STATE_LABELS[state] || humanise(state);
}

export function livenessLabel(liveness: string): string {
  return LIVENESS_LABELS[liveness] || humanise(liveness);
}

/** A value the server did not report, said in words rather than left blank. */
export function reported(value: string | null | undefined): string {
  return value ? value : "not reported";
}

function slug(value: string | null | undefined, fallback: string): string {
  const text = (value || "").trim();
  return text ? text.replace(/[^a-zA-Z0-9_-]+/g, "-") : fallback;
}

export function isInstances(
  body: ConsoleInstancesResponse | ConsoleRefusal,
): body is ConsoleInstancesResponse {
  return (body as ConsoleInstancesResponse).response_type === "console_instances";
}

export type InstancesBladeProps = {
  instances: ConsoleInstancesResponse | ConsoleRefusal | null;
  loading: boolean;
  onRefresh: () => void;
};

function MetaLine({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <p className={styles.cardLine}>
      <span className={styles.cardLabel}>{label}</span>
      <span className={styles.cardValue}>{children}</span>
    </p>
  );
}

function SharedCard({
  row,
  testId,
}: {
  row: ConsoleSharedInstanceRow;
  testId: string;
}) {
  const deployed = row.last_deployed_at
    ? `${row.last_deployed_at}${row.last_deployed_by ? ` by ${row.last_deployed_by}` : ""}`
    : row.last_deployed_by
      ? `not reported by ${row.last_deployed_by}`
      : "not reported";
  return (
    <article className={styles.card} data-testid={`instances-shared-${testId}`}>
      <h4 className={styles.cardTitle}>{reported(row.environment_name)}</h4>
      <MetaLine label="Status">{reported(row.status)}</MetaLine>
      <MetaLine label="Version">{reported(row.code_version)}</MetaLine>
      <MetaLine label="Database at">{reported(row.db_applied_order)}</MetaLine>
      <MetaLine label="Last deployed">{deployed}</MetaLine>
      <MetaLine label="Drift">
        <span
          className={`${styles.driftState} ${styles[`drift_${row.drift_state}`] || ""}`}
          data-testid={`instances-drift-state-${testId}`}
        >
          {driftStateLabel(row.drift_state)}
        </span>
      </MetaLine>
      {row.drift_reasons && row.drift_reasons.length > 0 ? (
        <details
          className={styles.detail}
          data-testid={`instances-drift-reasons-${testId}`}
        >
          <summary className={styles.detailSummary}>Why</summary>
          <ul className={styles.reasonList}>
            {row.drift_reasons.map((reason) => (
              <li key={reason} className={styles.reasonLine}>
                {reason}
              </li>
            ))}
          </ul>
        </details>
      ) : null}
    </article>
  );
}

export function InstancesBlade({ instances, loading, onRefresh }: InstancesBladeProps) {
  const answered = instances && isInstances(instances) ? instances : null;
  const message = instances?.message;
  // A refusal that never reached the registry carries no ``shared`` at all
  // (undefined), never an empty list - so a section is shown only once the read
  // actually happened, the same way the Overview only shows Fleet once ``fleet`` is
  // present.
  const shared = answered?.shared;

  return (
    <div className={styles.blade} data-testid="console-instances-blade">
      <div className={styles.header}>
        <div>
          <h2 className={styles.title}>Cordexa instances</h2>
          {answered?.scope_statement ? (
            <p className={styles.scopeStatement} data-testid="instances-scope-statement">
              {answered.scope_statement}
            </p>
          ) : null}
        </div>
        <button
          type="button"
          className={styles.refresh}
          data-testid="instances-refresh"
          onClick={onRefresh}
        >
          Refresh
        </button>
      </div>

      {message ? (
        <p className={styles.message} data-testid="instances-message">
          {message}
        </p>
      ) : null}

      {loading ? (
        <ContentRegionLoader label="Loading Instances" data-testid="instances-loading" />
      ) : null}

      {!loading && answered && shared ? (
        <section className={styles.section} data-testid="instances-shared">
          <h3 className={styles.sectionTitle}>Shared environments</h3>
          {shared.length === 0 ? (
            <p className={styles.emptySection}>
              No shared environments are visible to you.
            </p>
          ) : (
            <div className={styles.cardGrid}>
              {shared.map((row, index) => (
                <SharedCard
                  key={row.environment_name || index}
                  row={row}
                  testId={slug(row.environment_name, String(index))}
                />
              ))}
            </div>
          )}
        </section>
      ) : null}
    </div>
  );
}
